import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {AbstractControl, FormControl, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms'
import {ActivatedRoute, Router} from '@angular/router'
import {
  DeleteJobFileGql,
  GenerateJobUploadLinkGql,
  JobRequestDetail_ValidateVoucherGql,
  OfferingType,
  VoucherTerm,
  VoucherValidationError,
} from '../../../../../__generated__/types'
import {map, startWith, takeUntil, tap} from 'rxjs/operators'
import {FileModel} from '../../../../craft/files/file.model'
import {Observable, of, Subject} from 'rxjs'
import {FileDeleteResult} from '../../../../craft/files/files.component'
import {JobRequestRepository, PhotosAnchorTag, VoucherAnchorTag} from '../job-request.repository'
import {DynamicForm} from '../../../../shared/components/dynamic-form/dynamic-form.model'
import {DynamicOfferingDetailsService} from '../../../../core/job/dynamic-offering-details.service'
import {JobRequestService} from '../job-request.service'
import {StaticComponentInstance} from './static-form/static-form.types'
import {DocumentService} from '../../../../craft/utils/document.service'
import {FooterProgressSteps} from '../job-request-footer/job-request-footer.component'
import {ProcessService} from '../../../../shared/components/process-navbar/process.service'
import {OfferingResolved} from '../resolvers/job-request-offering.resolver'
import {AgentResolved} from '../resolvers/job-request-agent.resolver'
import {TypedFormGroup} from '../../../../core/forms/form-controls.model'
import {DateInThePastValidator} from '../../../../core/date/date.validator'
import {resolveStaticFormForPriceRangeOffering} from './static-form/static-form.service'
import {AgencyResolved} from '../resolvers/job-request-agency.resolver'
import {indicate} from '../../../../core/graphql/indicate'
import {PreferredProResolved} from '../resolvers/job-request-preferred-pro.resolver'
import {QueryParamsService} from '../../../../core/query-params/query-params.service'
import {VOUCHER_QUERY_PARAM} from '../../../../core/query-params/query-params.types'
import {faCheck} from '@fortawesome/pro-solid-svg-icons/faCheck'
import {TranslocoService} from '@ngneat/transloco'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {JobRequestPageMetadataService} from '../job-request-page-metadata.service'
import {GenerateFileUploadLinkFunction} from '../../../../craft/files/file.service.types'

@Component({
  templateUrl: './job-request-detail-page.component.html',
  styleUrls: ['./gradual-reveal.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {ngSkipHydration: 'true'}, //eslint-disable-line @angular-eslint/no-host-metadata-property
})
export class JobRequestDetailPageComponent extends PageComponent implements OnInit, OnDestroy {
  readonly checkmarkIcon = faCheck
  readonly jobTitleMaxLength = 50
  readonly supportedTypes = ['image/png', 'image/jpeg', 'application/pdf']

  readonly offering?: OfferingResolved = this.route.snapshot.data['offering']
  readonly agent?: AgentResolved = this.route.snapshot.data['agent']
  readonly agency?: AgencyResolved = this.route.snapshot.data['agency']
  readonly pro?: PreferredProResolved = this.route.snapshot.data['pro']
  readonly isDamageRequest = this.route.snapshot.queryParamMap.get('damage') && !!this.agent
  // Only get the intentionSlug and clientExpectation from session storage if activated for the offering.
  readonly intentionSlug = this.offering?.areIntentionsEnabled ? this.repository.getIntentionSlug() : undefined

  staticQuestionForm? = this.offering ? resolveStaticFormForPriceRangeOffering(this.offering)?.createForm() : undefined
  staticFormActivated = this.staticQuestionForm ? false : undefined
  staticFormInstance?: StaticComponentInstance

  offeringQuestionForm?: DynamicForm = (() => {
    // horrendous logic
    if (this?.offering && !this.staticQuestionForm) {
      if (this.intentionSlug) {
        const intention = this.offering.intentions?.find(i => i.slug === this.intentionSlug)
        if (intention) {
          return new DynamicForm(DynamicOfferingDetailsService.toClientFacingQuestions(intention.questions!))
        }
      } else if (this.offering?.questions?.length) {
        return new DynamicForm(DynamicOfferingDetailsService.toClientFacingQuestions(this.offering.questions!))
      }
    }
    return undefined
  })()

  readonly damageFormControls = {
    caseNumber: new UntypedFormControl(undefined, [
      this.isDamageRequest ? Validators.required : Validators.nullValidator,
    ]),
    occurredAt: new UntypedFormControl(undefined, [
      this.isDamageRequest ? Validators.required : Validators.nullValidator,
      DateInThePastValidator({allowToday: true}),
    ]),
    agentDirectives: new UntypedFormControl(undefined),
  }
  readonly damageForm = new TypedFormGroup<{caseNumber: string; occurredAt: Date; directives?: string}>(
    this.damageFormControls,
  )

  readonly controls = {
    title: new UntypedFormControl('', [
      this.offering ? Validators.nullValidator : Validators.required,
      Validators.maxLength(this.jobTitleMaxLength),
    ]),
    description: new UntypedFormControl('', [
      this.offering ? Validators.nullValidator : Validators.required,
      Validators.maxLength(2000),
    ]),
    files: new UntypedFormControl(),
    offeringQuestionForm: this.offeringQuestionForm,
    staticQuestionForm: this.staticQuestionForm,
    addPhotos: new UntypedFormControl(undefined),
    hasVoucher: new FormControl<boolean>(false),
    voucherCode: new UntypedFormControl(undefined),
    damage: this.damageForm,
  }
  readonly form = this.fb.group(this.controls)

  /** form progress (regardless of required/optional questions */
  readonly progressSteps: FooterProgressSteps = {
    currentStep$: this.form.valueChanges.pipe(
      startWith(this.form),
      map(() => {
        return this.controlsForProgressCalculation.filter(c => c.valid).length
      }),
    ),
    numberOfSteps: this.controlsForProgressCalculation.length,
  }

  /** required form progress, only when rendering an offering dynamic form */
  protected readonly offeringRequiredFormProgress$: Observable<{required: number; answered: number} | undefined> =
    this.offeringQuestionForm?.valueChanges.pipe(
      startWith(this.offeringQuestionForm?.value),
      map(() => {
        if (!this.offeringQuestionForm) return undefined
        return {
          required: this.offeringQuestionForm.answerFormGroups.filter(a => a.question.required).length,
          answered: this.offeringQuestionForm.answerFormGroups.filter(a => a.valid && a.question.required).length,
        }
      }),
    ) ?? of(undefined)

  nextButtonText$: Observable<string> = this.offeringRequiredFormProgress$.pipe(
    map(formProgress => {
      if (this.staticFormActivated !== undefined) {
        return this.translationService.translate('createJobRequest.add')
      } else if (formProgress && formProgress.required > 0 && formProgress.answered < formProgress.required) {
        return this.translationService.translate('createJobRequest.nextProgress', formProgress)
      } else {
        return this.translationService.translate('createJobRequest.next')
      }
    }),
  )

  readonly OfferingType = OfferingType

  readonly voucherValidating$ = new Subject<boolean>()
  readonly voucherValidated$ = new Subject<{
    success: boolean
    error?: VoucherValidationError
    voucher?: {id: string; terms: VoucherTerm[]}
  }>()
  readonly VoucherValidationError = VoucherValidationError

  private readonly nextPath = PUBLIC_ROUTES.JOB_REQUEST_EXECUTION_TIME
  private readonly unsubscribe$ = new Subject<void>()

  constructor(
    readonly jobRequestService: JobRequestService,
    readonly processService: ProcessService,
    readonly repository: JobRequestRepository,
    private readonly fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly generateJobUploadLinkGql: GenerateJobUploadLinkGql,
    private readonly deleteDocumentGql: DeleteJobFileGql,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly documentService: DocumentService,
    private readonly validateVoucherGql: JobRequestDetail_ValidateVoucherGql,
    private readonly queryParamsService: QueryParamsService,
    private readonly translationService: TranslocoService,
    private readonly jobRequestPageMetadataService: JobRequestPageMetadataService,
  ) {
    super()
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.jobRequestPageMetadataService.getPageMetadataForStep('DETAILS', this.offering)
  }

  get controlsForProgressCalculation(): AbstractControl[] {
    let controls: AbstractControl[]
    if (this.controls.staticQuestionForm) {
      controls = Object.values(this.controls.staticQuestionForm!.controls)
    } else if (this.controls.offeringQuestionForm) {
      controls = Object.values(this.controls.offeringQuestionForm.controls)
    } else {
      controls = [this.controls.title, this.controls.description]
    }

    if (this.isDamageRequest) {
      controls.push(...Object.values(this.damageFormControls))
    }

    return controls.concat([this.controls.addPhotos])
  }

  ngOnInit(): void {
    const clientExpectation = this.offering?.showClientExpectation
      ? this.repository.readClientExpectationState()
      : undefined
    if (!this.intentionSlug && !clientExpectation) {
      this.jobRequestService.requestProcessDidStart(this.offering?.id)
    }
    this.hydrateFormState()
    this.observeDocuments()

    const voucher = this.queryParamsService.readQueryParamFromStorage(VOUCHER_QUERY_PARAM)
    if (voucher) {
      this.controls.hasVoucher.setValue(true)
      this.controls.voucherCode.setValue(voucher)
    }
    if (this.controls.voucherCode.value) {
      this.validateVoucher(this.controls.voucherCode.value, {ignoreWhenInvalid: true})
    }
    this.controls.hasVoucher.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(hasVoucher => {
      if (hasVoucher) {
        this.documentService.scrollToBottom()
      }
    })
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  private observeDocuments() {
    this.controls.files?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.cdr.markForCheck()
    })
  }

  readonly generateUploadLink: GenerateFileUploadLinkFunction = file => {
    return this.generateJobUploadLinkGql.mutate({file}).pipe(
      map(res => {
        if (!res.data) {
          throw new Error(`Could not generate upload link for file ${file.fileName}`)
        }
        return res.data.generateJobFileUploadLink
      }),
    )
  }

  readonly deleteFile = (file: FileModel): Observable<FileDeleteResult> => {
    return this.deleteDocumentGql
      .mutate({
        request: {
          fileKey: file.fileKey,
        },
      })
      .pipe(
        map(res => {
          return {
            err: res.data?.deleteJobFile.errorMessage || 'Unknown error when deleting file',
            success: res.data?.deleteJobFile.success || false,
          }
        }),
        tap(res => {
          if (res.success) {
            this.persistCurrentFormState()
          }
        }),
      )
  }

  next(): void {
    this.form.markAllAsTouched()
    if (this.staticQuestionForm) {
      this.staticQuestionForm.markAllAsTouched()
    }

    if (this.form.valid) {
      this.persistCurrentFormState()
      this.router.navigate([this.nextPath.routingPath.replace(':intentionSlug', this.intentionSlug ?? '')], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      })
    }
  }

  activateForm(activated: boolean): void {
    this.staticFormActivated = activated
    setTimeout(() => this.cdr.detectChanges())
  }

  staticFormInstantiated(instance: StaticComponentInstance): void {
    this.staticFormInstance = instance
    const state = this.repository.readStaticFormState()
    if (state?.staticForm) {
      instance.form.patchValue(state.staticForm)
    }
    if (state?.extra && instance.hydrateExtraInfo) {
      instance.hydrateExtraInfo(state.extra)
    }
  }

  validateVoucher(voucherCode: string, options: {e?: MouseEvent; ignoreWhenInvalid?: boolean}) {
    options.e?.preventDefault()
    options.e?.stopPropagation()

    this.validateVoucherGql
      .fetch({voucherCode})
      .pipe(
        indicate(this.voucherValidating$),
        map(res => res.data.validateVoucher),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(res => {
        if (res.__typename === 'Voucher') {
          this.voucherValidated$.next({success: true, voucher: res})
        } else {
          if (options.ignoreWhenInvalid) {
            this.controls.voucherCode.setValue(undefined)
            this.controls.hasVoucher.setValue(false)
          } else {
            this.voucherValidated$.next({success: false, error: res.error})
          }
        }
      })
  }

  private hydrateFormState() {
    const state = this.repository.readJobRequestDetailState()
    if (state) {
      this.form.patchValue(state)
    }
    const filesState = this.repository.readJobRequestFilesState()
    if (filesState?.files) {
      this.controls.files.setValue(filesState.files)
    }
    const offeringQuestionState = this.repository.readDynamicFormState()
    if (this.offeringQuestionForm && offeringQuestionState) {
      this.offeringQuestionForm.patchValue(offeringQuestionState)
    }
    const damage = this.repository.readJobRequestDamageState()
    if (damage) {
      this.damageForm.patchValue(damage)
    }
  }

  private persistCurrentFormState() {
    const referrer = this.jobRequestService.referrerPage()
    this.repository.storeJobRequestDetailState({...this.form.value, referrer})
    this.repository.storeJobRequestFilesState(this.form.value)
    if (this.agency?.jobRequestCode) {
      this.repository.storeStakeholderState({agencyJobRequestCode: this.agency.jobRequestCode})
    }
    if (this.pro?.proId) {
      this.repository.storePreferredProState(this.pro)
    }
    if (this.offering) {
      if (this.offeringQuestionForm) {
        this.repository.storeDynamicFormState(this.offeringQuestionForm.value)
      }
      // we might have an offering without a question form, but we still need to store the offering id
      this.repository.storeJobRequestOfferingState({
        id: this.offering.id,
        questions:
          this.offeringQuestionForm?.toAnswers().map(a => ({
            id: a.questionId,
            answerIds: a.answerIds,
            answerValue: a.answerValue,
            additionalInformation: a.additionalInformation,
          })) || [],
      })
    }

    if (this.staticFormInstance && this.staticQuestionForm) {
      const extra = this.staticFormInstance.saveExtraInfo ? this.staticFormInstance.saveExtraInfo() : undefined
      this.repository.storeStaticFormState({staticForm: this.staticQuestionForm.getRawValue(), extra})
      this.repository.storeJobRequestOfferingState({
        id: this.offering!.id,
        questions: [],
        staticQuestions: this.staticFormInstance.questionsInput ? this.staticFormInstance.questionsInput() : undefined,
      })
    }

    if (this.isDamageRequest) {
      this.repository.storeJobRequestDamageState({
        caseNumber: this.damageFormControls.caseNumber.value,
        occurredAt: this.damageFormControls.occurredAt.value,
        agentJobRequestCode: this.agent!.jobRequestCode!,
        agentDirectives: this.damageFormControls.agentDirectives.value,
      })
    }
  }

  protected readonly VoucherAnchorTag = VoucherAnchorTag
  protected readonly PhotosAnchorTag = PhotosAnchorTag
}
